import { useEffect, useState } from 'react'
import { Swords, Trophy, XCircle, RefreshCw, Shield, ChevronDown, Crosshair, Skull, Zap } from 'lucide-react'
import { api } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { Battle, BattleUnit, BattleHighlight } from '@territory-nexus/shared'
import { UNIT_META } from '@territory-nexus/shared'
import clsx from 'clsx'
import { getHighlightIcon } from './BattleResultOverlay'

interface BattleHistoryProps {
  onSelectZone?: (zoneId: string) => void
}

export default function BattleHistory({ onSelectZone }: BattleHistoryProps) {
  const { player } = useAuthStore()
  const [battles, setBattles] = useState<Battle[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedBattle, setExpandedBattle] = useState<string | null>(null)

  const fetchBattles = async () => {
    try {
      setLoading(true)
      setError(null)

      const historyRes = await api.get<{ success: boolean; data: Battle[] }>('/battles/history')
      if (historyRes.data.success) {
        setBattles(historyRes.data.data)
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to load battles')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBattles()
  }, [])

  const formatTime = (date: Date | string) => {
    const d = new Date(date)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  const isWinner = (battle: Battle) => battle.winnerId === player?.id
  const isAttacker = (battle: Battle) => battle.attackerId === player?.id

  const getBattleStats = (battle: Battle) => {
    const myUnits = isAttacker(battle) ? battle.attackerUnits : (battle.defenderUnits || [])
    const enemyUnits = isAttacker(battle) ? (battle.defenderUnits || []) : battle.attackerUnits
    const myUnitIds = new Set(myUnits.map((u: BattleUnit) => u.id))

    let totalDamage = 0
    let totalCrits = 0
    let totalAttacks = 0
    const log = battle.battleLog || []

    for (const action of log) {
      if (myUnitIds.has(action.unitId) && action.damage) {
        totalDamage += action.damage
        totalAttacks++
        if (action.isCrit) totalCrits++
      }
    }

    const enemiesKilled = enemyUnits.filter((u: BattleUnit) => (u.currentHp ?? u.stats.hp) <= 0).length
    const myLosses = myUnits.filter((u: BattleUnit) => (u.currentHp ?? u.stats.hp) <= 0).length
    const won = isWinner(battle)
    const baseXp = 50
    const winBonus = won ? 100 : 0
    const killBonus = enemiesKilled * 25
    const totalXp = (baseXp + winBonus + killBonus) * myUnits.length

    return { totalDamage, totalCrits, totalAttacks, enemiesKilled, myLosses, totalXp, myUnits, enemyUnits }
  }


  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-bold text-white">Battles</h2>
          <button
            onClick={fetchBattles}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="glass border border-red-500/30 bg-red-500/10 rounded-lg p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Battle History */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
            <Swords className="w-4 h-4" />
            Battle History
          </h3>

          {battles.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center border border-white/5">
              <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 mb-2">No battles yet</p>
              <p className="text-slate-500 text-sm">
                Attack enemy zones or corrupted zones to start battling!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {battles.map((battle) => {
                const won = isWinner(battle)
                const attacking = isAttacker(battle)
                const isPvE = battle.battleType === 'pve'
                const isExpanded = expandedBattle === battle.id
                const stats = isExpanded ? getBattleStats(battle) : null
                const highlights: BattleHighlight[] = (battle.highlights as BattleHighlight[]) || []

                return (
                  <div
                    key={battle.id}
                    className={clsx(
                      'glass rounded-lg border transition',
                      won
                        ? 'border-emerald-500/20 bg-emerald-500/5'
                        : 'border-red-500/20 bg-red-500/5'
                    )}
                  >
                    <button
                      onClick={() => setExpandedBattle(isExpanded ? null : battle.id)}
                      className="w-full p-4 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={clsx(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            won ? 'bg-emerald-500/20' : 'bg-red-500/20'
                          )}
                        >
                          {won ? (
                            <Trophy className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">
                              {won ? 'Victory' : 'Defeat'} - {attacking ? 'Attack' : 'Defense'}
                            </p>
                            <span className={clsx(
                              'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold',
                              isPvE
                                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                                : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            )}>
                              {isPvE ? 'PvE' : 'PvP'}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400">
                            Zone #{battle.zoneId.slice(-6).toUpperCase()} • {formatTime(battle.endedAt || battle.startedAt)}
                          </p>
                        </div>
                      </div>
                      <ChevronDown className={clsx('w-4 h-4 text-slate-400 transition-transform', isExpanded && 'rotate-180')} />
                    </button>

                    {/* Expanded Battle Report */}
                    {isExpanded && stats && (
                      <div className="px-4 pb-4 border-t border-white/5 pt-3 space-y-3">
                        {/* Summary Stats */}
                        <div className="grid grid-cols-4 gap-2">
                          <div className="text-center p-2 rounded-lg bg-white/5">
                            <Crosshair className="w-3.5 h-3.5 mx-auto text-orange-400 mb-1" />
                            <p className="text-sm font-semibold text-white">{stats.totalDamage}</p>
                            <p className="text-[9px] text-slate-500 uppercase">Damage</p>
                          </div>
                          <div className="text-center p-2 rounded-lg bg-white/5">
                            <Skull className="w-3.5 h-3.5 mx-auto text-red-400 mb-1" />
                            <p className="text-sm font-semibold text-white">{stats.enemiesKilled}</p>
                            <p className="text-[9px] text-slate-500 uppercase">Kills</p>
                          </div>
                          <div className="text-center p-2 rounded-lg bg-white/5">
                            <Zap className="w-3.5 h-3.5 mx-auto text-amber-400 mb-1" />
                            <p className="text-sm font-semibold text-white">{stats.totalCrits}</p>
                            <p className="text-[9px] text-slate-500 uppercase">Crits</p>
                          </div>
                          <div className="text-center p-2 rounded-lg bg-white/5">
                            <Trophy className="w-3.5 h-3.5 mx-auto text-cyan-400 mb-1" />
                            <p className="text-sm font-semibold text-cyan-400">+{stats.totalXp}</p>
                            <p className="text-[9px] text-slate-500 uppercase">XP</p>
                          </div>
                        </div>

                        {/* Unit Performance */}
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Your Units</p>
                          <div className="space-y-1">
                            {stats.myUnits.map((unit: BattleUnit) => {
                              const meta = UNIT_META[unit.unitType]
                              const hp = unit.currentHp ?? unit.stats.hp
                              const alive = hp > 0
                              const hpPercent = Math.max(0, Math.min(100, (hp / unit.stats.hp) * 100))
                              return (
                                <div key={unit.id} className={clsx('flex items-center gap-2 p-1.5 rounded', !alive && 'opacity-40')}>
                                  <span className={clsx('text-xs font-medium w-20 truncate', meta?.color || 'text-white')}>
                                    {meta?.shortName || unit.unitType}
                                  </span>
                                  <div className="flex-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                                    <div
                                      className={clsx(
                                        'h-full rounded-full',
                                        hpPercent > 50 ? 'bg-emerald-400' : hpPercent > 25 ? 'bg-amber-400' : 'bg-red-400'
                                      )}
                                      style={{ width: `${hpPercent}%` }}
                                    />
                                  </div>
                                  <span className="text-[10px] text-slate-400 w-12 text-right">
                                    {alive ? `${hp}/${unit.stats.hp}` : 'KIA'}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </div>

                        {/* Key Moments (Highlights) */}
                        {highlights.length > 0 && (
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Key Moments</p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {highlights.slice(0, 10).map((h, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-[11px]">
                                  <span className="mt-0.5 flex-shrink-0">{getHighlightIcon(h.type)}</span>
                                  <span className="text-slate-300">
                                    <span className="text-slate-500">T{h.turn}</span> {h.description}
                                  </span>
                                </div>
                              ))}
                              {highlights.length > 10 && (
                                <p className="text-[10px] text-slate-500">+{highlights.length - 10} more moments...</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Losses */}
                        {stats.myLosses > 0 && (
                          <p className="text-xs text-red-400/70">
                            {stats.myLosses} unit{stats.myLosses > 1 ? 's' : ''} lost in battle
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
